// Dependencies
import React, { useState, useRef } from 'react';

import appendQuery from 'append-query';
import classnames from 'classnames';
import Headroom from 'react-headroom';

import { Type, Text, Box } from '@nerdwallet/currency';
import elementInteraction from '@nerdwallet/nw-client-lib/lib/tracking/element-interaction';

// CSS
import './nav.less';

interface ItemChildren {
  text: string;
  href: string;
  target?: string;
  app?: string;
}

interface NavItems {
  text: string;
  href: string;
  target?: string;
  item?: string;
  children?: ItemChildren[];
}

interface NavProps {
  app: string;
  title: string;
  items: NavItems[];
}

// Constants
const NAV_OPENED_CLASS = 'nav-opened';

export const Nav: React.FC<NavProps> = (props) => {
  const { app, title, items } = props;

  const [opened, setOpened] = useState(false);
  const [topOffSet, setTopOffSet] = useState(0);
  const navRef = useRef(null);

  const appendTracking = (href) => {
    return appendQuery(href, {
      trk: `nw_${app}_leftRail`,
    });
  };

  const toggleMenu = () => {
    setOpened(!opened);
    setTopOffSet(navRef.current.getBoundingClientRect().top);

    elementInteraction({
      elementName: 'resource__section-navigation',
      interactionType: opened ? 'COLLAPSE' : 'EXPAND',
      elementDomType: 'button',
      elementContent: title,
    });

    if (typeof document?.body !== 'undefined') {
      document.body.classList.toggle(NAV_OPENED_CLASS, !opened);
    }
  };

  const renderTitle = () => {
    return (
      <div className="nav-title" onClick={toggleMenu}>
        <div>
          <Type
            size={1}
            color="neutral-darker"
            className="mobile"
            uppercase
            bold
          >
            MORE IN
          </Type>
          <Text bold>{title}</Text>
        </div>
        <div
          className={`mobile nav-title-arrow ${
            opened ? 'ss-navigateup' : 'ss-navigatedown'
          }`}
        />
      </div>
    );
  };

  const renderItems = (itemArr, level = 0) => {
    if (!itemArr) return null;

    const isActive = (appItem: string) => app === appItem;

    return (
      <ul className={`nav-items-level-${level}`}>
        {itemArr.map((navItem) => {
          const { children, href, target, text, item } = navItem;

          const itemText = (
            <Box
              paddingLeft="1"
              paddingTop="1"
              className={classnames('nav-items-text', {
                link: !!href,
                active: isActive(item),
              })}
            >
              <Text size="small" bold={level === 0}>
                {text}
              </Text>
            </Box>
          );

          return (
            <li key={text}>
              {href ? (
                <a href={appendTracking(href)} target={target}>
                  {itemText}
                </a>
              ) : (
                itemText
              )}
              {children ? renderItems(children, level + 1) : null}
            </li>
          );
        })}
      </ul>
    );
  };

  const renderNavItems = () => {
    return <div className="nav-items">{renderItems(items)}</div>;
  };

  let globalNavHeight;

  if (typeof window !== 'undefined') {
    globalNavHeight = document.getElementById('global-nav').offsetHeight;
  }

  return (
    <div className="nav" ref={navRef}>
      <div className="mobile">
        <Headroom pinStart={globalNavHeight}>{renderTitle()}</Headroom>
      </div>
      <div
        className={classnames('nav-opened-content', { opened })}
        style={{ top: topOffSet > 0 ? topOffSet : 0 }}
      >
        {renderTitle()}
        {renderNavItems()}
      </div>
    </div>
  );
};

export default Nav;
